"use client"

import type React from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { getCookie, setCookie } from "cookies-next"
import { dictionaries, type Dictionary, type Locale } from "@/lib/dictionaries"
import { DEFAULT_LOCALE, isLocale, toLocale } from "@/lib/translations"

interface LanguageContextType {
  language: Locale
  setLanguage: (language: Locale) => void
  dictionary: Dictionary
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const LOCALE_COOKIE = "NEXT_LOCALE"
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * The language comes from the FBO host, which appends the signed-in user's culture to
 * the iframe URL as ?culture=; a culture that is not French reads as English. The choice
 * is kept in a cookie so a reload without the parameter keeps the language. The browser's
 * own language is deliberately not consulted: the user's FBO profile decides.
 */
function initialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE
  const culture = new URLSearchParams(window.location.search).get("culture")
  if (culture) return toLocale(culture)
  const stored = getCookie(LOCALE_COOKIE)?.toString()
  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Locale | null>(null)

  const setLanguage = useCallback((next: Locale) => {
    setLanguageState(next)
    setCookie(LOCALE_COOKIE, next, { maxAge: ONE_YEAR_SECONDS })
    document.documentElement.lang = next
  }, [])

  useEffect(() => {
    setLanguage(initialLocale())
    // The host can navigate the iframe with a different culture; back/forward re-reads it
    const onNavigate = () => setLanguage(initialLocale())
    window.addEventListener("popstate", onNavigate)
    return () => window.removeEventListener("popstate", onNavigate)
  }, [setLanguage])

  const value = useMemo<LanguageContextType | null>(
    () => (language ? { language, setLanguage, dictionary: dictionaries[language] } : null),
    [language, setLanguage]
  )

  // Nothing renders until the language is known, so no string flashes in the wrong language
  if (!value) return null

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
